import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function EmailCampaignsPage() {
  const [tab, setTab] = useState('compose'); // compose | history
  const [audience, setAudience] = useState('all');
  const [audienceCounts, setAudienceCounts] = useState({ all: 0, contact_form: 0, order_form: 0, careers_form: 0 });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

  const fetchAudience = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email-campaigns/audience`, { headers });
      if (res.ok) setAudienceCounts(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/email-campaigns/history`, { headers });
      if (res.ok) setHistory(await res.json());
    } catch (err) { console.error(err); }
    setHistoryLoading(false);
  }, []);

  useEffect(() => { fetchAudience(); }, [fetchAudience]);
  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);

  const handleTestSend = async () => {
    if (!subject || !body) return setMessage({ type: 'error', text: 'Please enter a subject and body' });
    if (!testEmail) return setMessage({ type: 'error', text: 'Please enter a test email address' });
    setSending(true); setMessage(null);
    try {
      const res = await fetch(`${API}/api/email-campaigns/test`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject, body, testEmail })
      });
      const data = await res.json();
      setMessage({ type: res.ok ? 'success' : 'error', text: data.message || data.error });
    } catch (err) { setMessage({ type: 'error', text: 'Failed to send test email' }); }
    setSending(false);
  };

  const handleSend = async () => {
    setSending(true); setMessage(null); setShowConfirm(false);
    try {
      const res = await fetch(`${API}/api/email-campaigns/send`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject, body, audience })
      });
      const data = await res.json();
      setMessage({ type: res.ok ? 'success' : 'error', text: data.message || data.error });
      if (res.ok) { setSubject(''); setBody(''); }
    } catch (err) { setMessage({ type: 'error', text: 'Failed to send campaign' }); }
    setSending(false);
  };

  const recipientCount = audience === 'all' ? audienceCounts.all : audienceCounts[audience] || 0;

  const audienceOptions = [
    { key: 'all', label: 'All Subscribers', count: audienceCounts.all, icon: '📧' },
    { key: 'contact_form', label: 'Contact Form', count: audienceCounts.contact_form, icon: '✉️' },
    { key: 'order_form', label: 'Home Charger Orders', count: audienceCounts.order_form, icon: '🛒' },
    { key: 'careers_form', label: 'Job Applicants', count: audienceCounts.careers_form, icon: '💼' },
  ];

  return (
    <div style={{ padding: '32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Email Campaigns</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>Design and send emails to your subscriber lists</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#F3F4F6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {['compose', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            background: tab === t ? 'white' : 'transparent', color: tab === t ? '#111827' : '#6B7280',
            boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', textTransform: 'capitalize',
          }}>
            {t === 'compose' ? '✍️ Compose' : '📋 History'}
          </button>
        ))}
      </div>

      {message && (
        <div style={{
          padding: '14px 18px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600,
          background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          color: message.type === 'success' ? '#059669' : '#DC2626',
        }}>
          {message.text}
        </div>
      )}

      {tab === 'compose' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
          {/* Main editor */}
          <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #F3F4F6' }}>
              <label style={labelStyle}>Subject Line</label>
              <input
                type="text" value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Exciting news from LMPEnergy!"
                style={inputStyle}
              />
            </div>
            <div style={{ padding: '20px 24px' }}>
              <label style={labelStyle}>Email Body</label>
              <textarea
                value={body} onChange={e => setBody(e.target.value)}
                placeholder="Write your email here...\n\nUse blank lines to create paragraphs. Each subscriber will receive a personalised greeting and branded header automatically."
                rows={14}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }}
              />
              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                Emails are automatically branded with the LMPEnergy header, personalised greeting, and unsubscribe link.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Audience selector */}
            <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16 }}>
              <label style={labelStyle}>Audience</label>
              {audienceOptions.map(opt => (
                <label key={opt.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${audience === opt.key ? '#10B981' : '#E5E7EB'}`,
                  background: audience === opt.key ? '#F0FDF4' : 'white', marginBottom: 6,
                }}>
                  <input type="radio" name="audience" value={opt.key} checked={audience === opt.key}
                    onChange={() => setAudience(opt.key)}
                    style={{ accentColor: '#10B981' }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>{opt.icon} {opt.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#10B981', background: '#ECFDF5', padding: '2px 8px', borderRadius: 6 }}>{opt.count}</span>
                </label>
              ))}
            </div>

            {/* Actions */}
            <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16 }}>
              <label style={labelStyle}>Test Send</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="your@email.com" style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                />
                <button onClick={handleTestSend} disabled={sending}
                  style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                  Send Test
                </button>
              </div>

              <button onClick={() => setShowPreview(true)} disabled={!subject || !body}
                style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                👁️ Preview Email
              </button>

              <button onClick={() => { if (!subject || !body) { setMessage({ type: 'error', text: 'Please enter a subject and body' }); return; } setShowConfirm(true); }}
                disabled={sending || !subject || !body || recipientCount === 0}
                style={{
                  width: '100%', padding: 14, borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, color: 'white',
                  background: 'linear-gradient(135deg,#10B981,#3B82F6)', opacity: (!subject || !body || recipientCount === 0) ? 0.5 : 1,
                }}>
                {sending ? 'Sending...' : `📤 Send to ${recipientCount} subscriber${recipientCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {historyLoading ? (
            <Placeholder text="Loading..." />
          ) : history.length === 0 ? (
            <Placeholder text="No campaigns sent yet." />
          ) : (
            history.map(c => (
              <div key={c.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, marginBottom: 4 }}>{c.subject}</h3>
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#6B7280' }}>
                      <span>📧 {c.sent_count}/{c.total_recipients} delivered</span>
                      {c.failed_count > 0 && <span style={{ color: '#DC2626' }}>❌ {c.failed_count} failed</span>}
                      <span>🎯 {c.audience || 'all'}</span>
                      <span>👤 {c.first_name} {c.last_name}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 13, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleDateString('en-GB')} {new Date(c.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p style={{ fontSize: 14, color: '#6B7280', marginTop: 10, lineHeight: 1.6 }}>
                  {c.body?.substring(0, 150)}{c.body?.length > 150 ? '...' : ''}
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Preview modal */}
      {showPreview && (
        <div style={overlayStyle}>
          <div style={{ background: 'white', borderRadius: 20, padding: 0, maxWidth: 640, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>Email Preview</h3>
              <button onClick={() => setShowPreview(false)} style={closeBtnStyle}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ background: 'linear-gradient(135deg,#10B981,#3B82F6)', borderRadius: '12px 12px 0 0', padding: '24px 20px', textAlign: 'center' }}>
                <div style={{ color: 'white', fontSize: 10, opacity: 0.7, marginBottom: 8 }}>LMPEnergy</div>
                <h2 style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>{subject || 'Your Subject Line'}</h2>
              </div>
              <div style={{ background: '#FFF', padding: '24px 20px', border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 12px 12px' }}>
                <p style={{ color: '#6B7280', marginBottom: 16 }}>Hi [Name],</p>
                {(body || 'Your email body will appear here...').split('\n').filter(l => l.trim()).map((p, i) => (
                  <p key={i} style={{ color: '#374151', fontSize: 15, lineHeight: 1.7, marginBottom: 14 }}>{p}</p>
                ))}
                <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
                  <span style={{ display: 'inline-block', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>Visit LMPEnergy</span>
                </div>
              </div>
              <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 11, marginTop: 12 }}>
                LMPEnergy Ltd · Stevenage, UK · <u>Unsubscribe</u>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirm send modal */}
      {showConfirm && (
        <div style={overlayStyle}>
          <div style={{ background: 'white', borderRadius: 20, padding: 32, maxWidth: 440, width: '90%', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>⚠️</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Confirm Send</h2>
            <p style={{ color: '#6B7280', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
              You are about to send <strong>"{subject}"</strong> to <strong>{recipientCount} subscriber{recipientCount !== 1 ? 's' : ''}</strong>.
            </p>
            <p style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 24 }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Cancel</button>
              <button onClick={handleSend} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Placeholder({ text }) {
  return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', background: 'white', borderRadius: 16, border: '1px solid #E5E7EB' }}>{text}</div>;
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 };
const inputStyle = { width: '100%', padding: '12px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', background: '#F9FAFB', marginBottom: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const closeBtnStyle = { background: '#F3F4F6', border: 'none', width: 32, height: 32, borderRadius: 8, fontSize: 16, cursor: 'pointer', color: '#6B7280' };
